/** Live Telegram WebApp user (avatar is often only here, not in JWT profile). */
export function getTelegramUser() {
  try {
    return window.Telegram?.WebApp?.initDataUnsafe?.user || null;
  } catch {
    return null;
  }
}

function pickSafeAvatarUrl(url) {
  if (!url || typeof url !== 'string') return '';
  const u = url.trim();
  if (!u) return '';

  // Local cache: /media/avatars/<tg_id>.jpg optional ?v=mtime
  if (/^\/media\/avatars\/[0-9]+\.(jpg|jpeg|png|webp)(\?[A-Za-z0-9_.=-]*)?$/i.test(u)) {
    return u;
  }

  try {
    const parsed = new URL(u, typeof window !== 'undefined' ? window.location.origin : 'https://tap-mars.space');
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return '';

    // Same-origin (or absolute) local avatars
    if (/^\/media\/avatars\/[0-9]+\.(jpg|jpeg|png|webp)$/i.test(parsed.pathname)) {
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      if (!origin || parsed.origin === origin) {
        return `${parsed.pathname}${parsed.search}`;
      }
    }

    const host = parsed.hostname.toLowerCase();
    // Telegram serves an SVG placeholder (flat coloured disc with initials baked
    // in a font we don't have) when a user has no photo — our own initials
    // avatar looks far better than a stray teal blob.
    if (/\.svg$/i.test(parsed.pathname)) return '';
    // Telegram CDN / userpic hosts only — never arbitrary https
    if (
      host === 't.me' ||
      host.endsWith('.t.me') ||
      host === 'telegram.org' ||
      host.endsWith('.telegram.org') ||
      host.endsWith('.telesco.pe')
    ) {
      return u;
    }
  } catch {
    return '';
  }
  return '';
}

export function resolveAvatarUrl(userData) {
  const fromApi = pickSafeAvatarUrl(userData?.photo_url);
  if (fromApi) return fromApi;

  // Live WebApp photo only for the signed-in player — never for leaderboard/clan rows
  const tg = getTelegramUser();
  if (!tg) return '';

  const myId = Number(tg.id);
  const theirs = Number(userData?.telegram_id ?? userData?.id);
  if (Number.isFinite(theirs) && theirs > 0) {
    return theirs === myId ? pickSafeAvatarUrl(tg.photo_url) : '';
  }

  // Explicit other-player payload ({ photo_url, first_name }) without id → initials, not my face
  if (userData && ('photo_url' in userData || 'first_name' in userData)) {
    return '';
  }

  return pickSafeAvatarUrl(tg.photo_url);
}

export function resolveDisplayName(userData) {
  const tg = getTelegramUser();
  const first = userData?.first_name || '';
  const last = userData?.last_name || '';
  const name = `${first} ${last}`.trim();
  if (name) return name;
  if (userData?.username) return userData.username;

  // Fall back to live TG identity only for self (or when no row payload)
  const theirs = Number(userData?.telegram_id ?? userData?.id);
  const myId = Number(tg?.id);
  if (userData && Number.isFinite(theirs) && theirs > 0 && theirs !== myId) {
    return 'Марсианин';
  }
  if (userData && ('first_name' in userData || 'photo_url' in userData) && !userData.first_name && !userData.username) {
    // Leaderboard row with empty name — don't show my name
    return 'Марсианин';
  }

  const live = `${tg?.first_name || ''} ${tg?.last_name || ''}`.trim();
  if (live) return live;
  return tg?.username || 'Марсианин';
}

export function initialsFromName(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return 'TM';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
}
